import {put, call} from 'redux-saga/effects';
import Privilege from "../../entities/privilege";
import * as Actions from "../actions/privilegeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type PrivilegeAction = {
    privilegeId: string;
    privilege: Privilege;
    privileges: Privilege[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOnePrivilege(action: PrivilegeAction) {
	const session = tabSession.captureGeneration();
	try {
		const privilege: Privilege = yield call(api.findOne, 'privilege', action.privilegeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_FETCH_ONE_SUCCESS, privilegeId: action.privilegeId, privilege});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_FETCH_ONE_FAILURE, privilegeId: action.privilegeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {PrivilegeAction} action
 */
export function* savePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ PRIVILEGE_SAVE saga', action.privilege?.id);
    
	if (action.privilege === undefined || action.privilege === null) {
		console.error('savePrivilege received a null');
		throw new Error('savePrivilege received a null');
	}

	if (!(action.privilege instanceof Privilege)) {
		action.privilege = new Privilege(action.privilege);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'privilege', action.privilege);
		if (addResult.isErr()) {
			console.error(`⮂ savePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.PRIVILEGE_SAVE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_SAVE, privilege: action.privilege});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.PRIVILEGE_SAVE_FAILURE, privilege: action.privilege, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {PrivilegeAction} action
 * @param {Privilege[]} action.privileges
 */
export function* saveManyPrivileges(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ PRIVILEGE_SAVE_MANY saga', action.privileges.map(privilege => privilege.id).join(','));
    
    if (!Array.isArray(action.privileges)) {
        console.error('saveManyPrivileges expected an array, received', action.privileges);
        throw new Error('saveManyPrivileges expected an array');
    }

	try {
		const toSave: Privilege[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.privileges.length; i++) {
			const privilege = action.privileges[i];
			privilege.persisted = false;
			toSave.push(privilege);
		}

		const addResult = yield call(idb.add, 'privilege', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyPrivileges: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.PRIVILEGE_SAVE_MANY_FAILURE, privileges: action.privileges, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_SAVE_MANY, privileges: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.PRIVILEGE_SAVE_MANY_FAILURE, privileges: action.privileges, status: 500, err});

		console.error(`Error saving ${action.privileges.length} privileges to database`, err);
	}
}

/**
 * @param {PrivilegeAction} action
 */
export function* removePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.privilege === undefined || action.privilege === null || !(action.privilege instanceof Privilege)) {
			console.error('removePrivilege expected type Privilege, received', action.privilege);
			throw new Error('removePrivilege expected type Privilege');
		}

	try {
		// Mark as deleted
		action.privilege.status = 'D';
		action.privilege.addedOn = newHRTimestamp();
		action.privilege.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'privilege', action.privilege);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.PRIVILEGE_REMOVE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_REMOVE, privilege: action.privilege});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_REMOVE_FAILURE, privilege: action.privilege, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Privilege.
	*
	* @param {PrivilegeAction} action
	*/
export function* restorePrivilege(action: PrivilegeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restorePrivilege expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.privilege === undefined || action.privilege === null) {
        console.error('restorePrivilege expected a Privilege, received null');
        throw new Error('restorePrivilege expected a Privilege, received null');
    }
    else if (!(action.privilege instanceof Privilege)) action.privilege = new Privilege(action.privilege); 
    
	try {
		// Mark as restored
		action.privilege.status = 'U';
		action.privilege.addedOn = newHRTimestamp();
		action.privilege.addedBy = userId;
		action.privilege.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'privilege', action.privilege);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restorePrivilege: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.PRIVILEGE_RESTORE_FAILURE, privilege: action.privilege, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.PRIVILEGE_RESTORE, privilege: action.privilege});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.PRIVILEGE_RESTORE_FAILURE, privilege: action.privilege, status: 500, err});

		console.error('Error restoring from database', err);
	}
}